import { useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Plus, Trash2 } from "lucide-react"
import type { PayloadMapping, SourceType } from "@/types"

const TEMPLATE_VARIABLES: Record<SourceType, string[]> = {
  webhook: ["title", "content", "date", "url", "source_name", "source_type", "category"],
  rss: ["title", "content", "date", "url", "author", "source_name", "source_type", "category"],
  podcast_rss: ["title", "content", "date", "url", "episode_number", "duration", "show_name", "source_name", "source_type", "category"],
  mimeo: ["title", "content", "date", "subject", "pre_header", "segment_name", "source_name", "source_type", "category"],
  youtube: ["title", "content", "date", "url", "video_id", "thumbnail_url", "duration", "description", "source_name", "source_type", "category"],
  discord: ["title", "content", "date", "channel_name", "thread_title", "server_name", "source_name", "source_type", "category"],
}

interface PayloadMappingEditorProps {
  mappings: PayloadMapping[]
  onChange: (mappings: PayloadMapping[]) => void
  sourceType: SourceType
}

export function PayloadMappingEditor({ mappings, onChange, sourceType }: PayloadMappingEditorProps) {
  const focusedValueRef = useRef<HTMLInputElement | null>(null)
  const variables = TEMPLATE_VARIABLES[sourceType] || []

  function addRow() {
    onChange([...mappings, { key: "", value: "" }])
  }

  function removeRow(index: number) {
    onChange(mappings.filter((_, i) => i !== index))
  }

  function updateRow(index: number, field: "key" | "value", val: string) {
    const updated = mappings.map((m, i) => (i === index ? { ...m, [field]: val } : m))
    onChange(updated)
  }

  function insertVariable(variable: string) {
    const input = focusedValueRef.current
    if (!input) return

    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    const before = input.value.slice(0, start)
    const after = input.value.slice(end)
    const inserted = `{{${variable}}}`
    const newValue = before + inserted + after

    // Find the index of this input in the mappings
    const index = Number(input.dataset.index)
    if (!isNaN(index)) {
      updateRow(index, "value", newValue)
      // Restore focus after React re-render
      requestAnimationFrame(() => {
        input.focus()
        const pos = start + inserted.length
        input.setSelectionRange(pos, pos)
      })
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-muted-foreground text-sm">
          Available variables (click to insert into focused value field):
        </p>
        <div className="flex flex-wrap gap-1">
          {variables.map((v) => (
            <Badge
              key={v}
              variant="outline"
              className="cursor-pointer hover:bg-accent"
              onClick={() => insertVariable(v)}
            >
              {`{{${v}}}`}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {mappings.map((mapping, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              placeholder="key name"
              value={mapping.key}
              onChange={(e) => updateRow(index, "key", e.target.value)}
              className="flex-1"
            />
            <Input
              placeholder="{{variable}} or literal value"
              value={mapping.value}
              onChange={(e) => updateRow(index, "value", e.target.value)}
              onFocus={(e) => {
                focusedValueRef.current = e.target
              }}
              data-index={index}
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => removeRow(index)}
            >
              <Trash2 className="size-4" />
              <span className="sr-only">Remove</span>
            </Button>
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRow}>
        <Plus className="size-4" />
        Add Field
      </Button>
    </div>
  )
}

export { TEMPLATE_VARIABLES }
